import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthSessionService } from '../auth/auth-session.service';
import { API_BASE_URL } from '../../shared/config/api.config';

// Endpoints de auth que nunca deben disparar un intento de refresh: un login,
// un refresh o un logout que fallan en 401 son el error real, no un token vencido.
// Match exacto (endsWith) en vez de substring: evita que un endpoint futuro que
// simplemente contenga una de estas rutas como substring matchee por accidente.
const AUTH_ENDPOINTS_WITHOUT_RETRY = ['/auth/login/admin', '/auth/admin/refresh', '/auth/admin/logout'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Scoping por origen: solo el gateway de este backend recibe credenciales y
  // el header Authorization. Cualquier otro request (assets, APIs de terceros)
  // pasa sin tocar.
  // Comparación de origen parseado (no startsWith) para no matchear URLs tipo
  // `${API_BASE_URL}.evil.example`. API_BASE_URL es siempre absoluta (environment).
  if (new URL(req.url, location.origin).origin !== new URL(API_BASE_URL).origin) {
    return next(req);
  }

  const authService = inject(AuthService);
  const session = inject(AuthSessionService);

  const token = session.accessToken();
  const isAuthEndpoint = AUTH_ENDPOINTS_WITHOUT_RETRY.some((path) => req.url.endsWith(path));

  // withCredentials siempre: el login admin setea cookies HttpOnly propias del
  // panel (admin_access_token, admin_refresh_token), separadas de las del sitio
  // público, que /auth/admin/refresh y /auth/admin/logout necesitan recibir/enviar.
  //
  // Hotspot de seguridad revisado: /auth/admin/refresh y /auth/admin/logout dependen de
  // cookies HttpOnly y no pasan por la validación del header JWT del gateway
  // (verificado en JwtAuthenticationGatewayFilterFactory de dm-gateway-ms, que
  // nunca lee cookies). Cualquier otro endpoint protegido exige el header
  // Authorization. Una request cross-site forjada a estos dos endpoints
  // cookie-only a lo sumo rota un token o fuerza un logout; no puede alcanzar
  // ni mutar ningún endpoint con datos de negocio reales.
  const authReq = req.clone({
    withCredentials: true,
    // Login/refresh/logout se autentican con credenciales propias o cookies
    // HttpOnly del backend; nunca deben heredar un Bearer viejo.
    headers: token && !isAuthEndpoint
      ? req.headers.set('Authorization', `Bearer ${token}`)
      : req.headers,
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      return authService.refreshToken().pipe(
        catchError((refreshError) => {
          // Una respuesta 401 o un refresh fallido no destruyen la sesión.
          // El cierre local queda reservado exclusivamente a Logout.
          return throwError(() => refreshError);
        }),
        switchMap((newToken) => {
          const retryReq = authReq.clone({
            headers: authReq.headers.set('Authorization', `Bearer ${newToken.access_token}`),
          });
          // Si el reintento falla por otro motivo (403/500/red), se propaga tal
          // cual: no limpia sesión ni redirige. next() va directo al backend, no
          // re-entra a este interceptor, así que no hay riesgo de loop.
          return next(retryReq);
        }),
      );
    }),
  );
};
